import { useSnapshot } from "valtio";
import {
  ResizablePanelGroup,
  ResizablePanel,
  ResizableHandle,
} from "@/components/ui/resizable";
import { themeEditorStore, setPanelSize } from "@/lib/stores/theme-editor";
import { ThemeEditorPanel } from "./ThemeEditorPanel";

interface ThemeEditorWrapperProps {
  children: React.ReactNode;
}

/**
 * Wrapper component that conditionally renders a split panel
 * with the theme editor when enabled
 */
export function ThemeEditorWrapper({ children }: ThemeEditorWrapperProps) {
  const { isOpen, panelSize } = useSnapshot(themeEditorStore);

  // When editor is closed, render children normally
  if (!isOpen) {
    return <>{children}</>;
  }

  // Ensure panelSize is reasonable (should be 25-80%)
  const effectivePanelSize = Math.max(25, Math.min(80, panelSize));
  const appPanelSize = 100 - effectivePanelSize;

  return (
    <ResizablePanelGroup direction="horizontal" className="h-full w-full">
      {/* Main app content */}
      <ResizablePanel defaultSize={appPanelSize} minSize={20}>
        {children}
      </ResizablePanel>

      {/* Draggable divider */}
      <ResizableHandle withHandle />

      {/* Theme editor panel */}
      <ResizablePanel
        defaultSize={effectivePanelSize}
        minSize={25}
        onResize={(size) => setPanelSize(size.asPercentage)}
      >
        <ThemeEditorPanel />
      </ResizablePanel>
    </ResizablePanelGroup>
  );
}
